<script setup>
import { computed, nextTick, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import { useArrowsAiAssistants } from 'dashboard/composables/useArrowsAiAssistants';
import { useArrowsAiFaqs } from 'dashboard/composables/useArrowsAiFaqs';
import { useArrowsAiCsvImport } from 'dashboard/composables/useArrowsAiCsvImport';
import {
  buildCsv,
  csvHeadersFor,
  downloadCsv,
} from 'dashboard/helper/arrowsAiCsv';
import PageLayout from 'dashboard/components-next/arrowsAi/PageLayout.vue';
import EmptyState from 'dashboard/components-next/arrowsAi/EmptyState.vue';
import MissingAssistant from 'dashboard/components-next/arrowsAi/MissingAssistant.vue';
import CsvActions from 'dashboard/components-next/arrowsAi/CsvActions.vue';
import BulkSelectBar from 'dashboard/components-next/arrowsAi/assistant/BulkSelectBar.vue';
import FaqCard from 'dashboard/components-next/arrowsAi/assistant/FaqCard.vue';
import FaqDialog from 'dashboard/components-next/arrowsAi/assistant/FaqDialog.vue';

const { t, locale } = useI18n();
const route = useRoute();
const { getAssistant, rememberAssistant } = useArrowsAiAssistants();
const assistantId = computed(() => Number(route.params.assistantId));
const assistant = computed(() => {
  const current = getAssistant(assistantId.value);
  if (current) rememberAssistant(current.id);
  return current;
});
const { faqs, isLoading, createFaq, updateFaq, deleteFaqs, importFaqs } =
  useArrowsAiFaqs(assistantId);
const { notifyResult, notifyError } = useArrowsAiCsvImport();
const csvActionsRef = ref(null);
const isImporting = ref(false);

const faqHeaders = computed(() =>
  csvHeadersFor(locale.value, ['question', 'answer'], ['pregunta', 'respuesta'])
);
const exampleContent = computed(() =>
  buildCsv(faqHeaders.value, [
    [t('ARROWS_AI.FAQS.EXAMPLE_Q1'), t('ARROWS_AI.FAQS.EXAMPLE_A1')],
    [t('ARROWS_AI.FAQS.EXAMPLE_Q2'), t('ARROWS_AI.FAQS.EXAMPLE_A2')],
  ])
);

const selectedIds = ref([]);
const selectedFaq = ref({});
const dialogRef = ref(null);

const selectedLabel = computed(() =>
  t('ARROWS_AI.FAQS.SELECTED', { count: selectedIds.value.length })
);
const isEmpty = computed(() =>
  Boolean(assistant.value && !isLoading.value && !faqs.value.length)
);
const ITEMS_PER_PAGE = 25;
const currentPage = ref(1);
const pagedFaqs = computed(() => {
  const start = (currentPage.value - 1) * ITEMS_PER_PAGE;
  return faqs.value.slice(start, start + ITEMS_PER_PAGE);
});

watch(
  () => faqs.value.length,
  total => {
    const maxPage = Math.max(1, Math.ceil(total / ITEMS_PER_PAGE));
    if (currentPage.value > maxPage) currentPage.value = maxPage;
  }
);

const openCreate = () => {
  selectedFaq.value = {};
  nextTick(() => dialogRef.value.dialogRef.open());
};

const openEdit = faq => {
  selectedFaq.value = faq;
  nextTick(() => dialogRef.value.dialogRef.open());
};

const handleSubmit = async payload => {
  if (!payload.question.trim() || !payload.answer.trim()) {
    useAlert(t('ARROWS_AI.FAQS.ERROR'));
    return;
  }

  try {
    if (selectedFaq.value.id) await updateFaq(selectedFaq.value.id, payload);
    else await createFaq(payload);
    dialogRef.value.dialogRef.close();
    await nextTick();
    useAlert(t('ARROWS_AI.FAQS.SUCCESS'));
  } catch (error) {
    useAlert(error.message || t('ARROWS_AI.FAQS.SAVE_ERROR'));
  }
};

const toggleSelect = id => {
  selectedIds.value = selectedIds.value.includes(id)
    ? selectedIds.value.filter(item => item !== id)
    : [...selectedIds.value, id];
};

const exportFaqs = () => {
  downloadCsv(
    csvHeadersFor(locale.value, 'faqs.csv', 'preguntas-frecuentes.csv'),
    buildCsv(
      faqHeaders.value,
      faqs.value.map(faq => [faq.question, faq.answer])
    )
  );
};

const handleImport = async file => {
  isImporting.value = true;
  try {
    const data = await importFaqs(file);
    csvActionsRef.value?.closeImport();
    await nextTick();
    notifyResult(data);
    currentPage.value = 1;
  } catch (error) {
    notifyError(error);
  } finally {
    isImporting.value = false;
  }
};

const removeFaqs = async ids => {
  try {
    await deleteFaqs(ids);
    selectedIds.value = selectedIds.value.filter(id => !ids.includes(id));
    useAlert(t('ARROWS_AI.FAQS.DELETED'));
  } catch (error) {
    useAlert(error.message || t('ARROWS_AI.FAQS.DELETE_ERROR'));
  }
};
</script>

<template>
  <PageLayout
    :header-title="t('ARROWS_AI.FAQS.TITLE')"
    :button-label="t('ARROWS_AI.FAQS.ADD')"
    :is-empty="isEmpty"
    :show-pagination-footer="Boolean(assistant)"
    :current-page="currentPage"
    :total-count="faqs.length"
    :items-per-page="ITEMS_PER_PAGE"
    @click="openCreate"
    @update:current-page="currentPage = $event"
  >
    <template v-if="assistant" #headerActions>
      <CsvActions
        ref="csvActionsRef"
        :example-filename="t('ARROWS_AI.FAQS.EXAMPLE_FILE')"
        :example-content="exampleContent"
        :is-importing="isImporting"
        @import="handleImport"
        @export="exportFaqs"
      />
    </template>
    <template #emptyState>
      <EmptyState
        :title="t('ARROWS_AI.FAQS.EMPTY_TITLE')"
        :subtitle="t('ARROWS_AI.FAQS.EMPTY_SUBTITLE')"
        :action-label="t('ARROWS_AI.FAQS.ADD')"
        @click="openCreate"
      />
    </template>
    <MissingAssistant v-if="!assistant" />
    <div v-else class="flex flex-col gap-4">
      <BulkSelectBar
        v-model="selectedIds"
        :items="pagedFaqs"
        :select-all-label="t('ARROWS_AI.FAQS.SELECT_ALL')"
        :unselect-all-label="t('ARROWS_AI.FAQS.UNSELECT_ALL')"
        :selected-label="selectedLabel"
        :delete-label="t('ARROWS_AI.FAQS.DELETE')"
        @bulk-delete="removeFaqs(selectedIds)"
      />
      <FaqCard
        v-for="faq in pagedFaqs"
        :key="faq.id"
        :faq="faq"
        :selected="selectedIds.includes(faq.id)"
        @select="toggleSelect"
        @edit="openEdit"
        @delete="removeFaqs([$event])"
      />
    </div>
  </PageLayout>
  <FaqDialog ref="dialogRef" :faq="selectedFaq" @submit="handleSubmit" />
</template>
